import { ResetFiltersButton } from "@/controls/buttons/ResetFiltersButton";
import { Autocomplete, Box, MenuItem, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import type { Moment } from "moment";
import { StandSelector } from "@/shared/selectors/StandSelector";

import type {
	CatalogOption,
	ProductSizeOption,
} from "../models/RFactorPerStand";

interface Props {
	start: Moment;
	end: Moment;
	productTypeId: number | null;
	productSizeId: number | null;
	crewId: string | null;
	standId: number | null;
	productTypes: CatalogOption[];
	productSizes: ProductSizeOption[];
	crews: string[];
	onStartChange: (date: Moment | null) => void;
	onEndChange: (date: Moment | null) => void;
	onProductTypeChange: (id: number | null) => void;
	onProductSizeChange: (id: number | null) => void;
	onCrewChange: (crew: string | null) => void;
	onStandChange: (id: number | null) => void;
	onReset: () => void;
	canReset: boolean;
}

// Same order the legacy lays them out, left to right. The Stand control is the
// one addition: the legacy renders it without a label, because the resource
// string for it was never written.
export const RFactorPerStandToolbar = ({
	start,
	end,
	productTypeId,
	productSizeId,
	crewId,
	standId,
	productTypes,
	productSizes,
	crews,
	onStartChange,
	onEndChange,
	onProductTypeChange,
	onProductSizeChange,
	onCrewChange,
	onStandChange,
	onReset,
	canReset,
}: Props) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			gap: 1,
			px: 1.5,
			py: 0.75,
			flexWrap: "nowrap",
		}}
	>
		<Box sx={{ flex: "0 0 150px" }}>
			<DatePicker
				label="Start"
				format="MM/DD/YYYY"
				value={start}
				maxDate={end}
				onChange={onStartChange}
				slotProps={{ textField: { size: "small", fullWidth: true } }}
			/>
		</Box>
		<Box sx={{ flex: "0 0 150px" }}>
			<DatePicker
				label="End"
				format="MM/DD/YYYY"
				value={end}
				minDate={start}
				onChange={onEndChange}
				slotProps={{ textField: { size: "small", fullWidth: true } }}
			/>
		</Box>

		<TextField
			select
			size="small"
			label="Product Type"
			slotProps={{
				inputLabel: { shrink: true },
				select: { displayEmpty: true },
			}}
			sx={{ flex: "0 0 170px" }}
			value={productTypeId ?? ""}
			onChange={(e) =>
				onProductTypeChange(
					e.target.value === "" ? null : Number(e.target.value),
				)
			}
		>
			<MenuItem value="">All</MenuItem>
			{productTypes.map((type) => (
				<MenuItem key={type.Id} value={type.Id}>
					{type.Name}
				</MenuItem>
			))}
		</TextField>

		{/* The mill runs well over a hundred sizes and a range can offer fifty of
		    them, so this one is typed rather than scrolled. Same control the
		    Product Changes report uses. */}
		<Autocomplete
			size="small"
			sx={{ flex: "0 0 230px" }}
			options={productSizes}
			value={productSizes.find((s) => s.Id === productSizeId) ?? null}
			getOptionLabel={(option) => option.Name}
			isOptionEqualToValue={(option, selected) => option.Id === selected.Id}
			onChange={(_event, option) => onProductSizeChange(option?.Id ?? null)}
			autoHighlight
			renderOption={(props, option) => {
				const { key, ...rest } = props as typeof props & { key: string };
				return (
					<li key={option.Id} {...rest}>
						{option.Name}
					</li>
				);
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					label="Product"
					// MUI hides the placeholder while the label still sits inside the
					// field, which is exactly when the hint is needed.
					slotProps={{ inputLabel: { shrink: true } }}
					placeholder="Search product..."
				/>
			)}
		/>

		<TextField
			select
			size="small"
			label="Shift"
			slotProps={{
				inputLabel: { shrink: true },
				select: { displayEmpty: true },
			}}
			sx={{ flex: "0 0 100px" }}
			value={crewId ?? ""}
			onChange={(e) =>
				onCrewChange(e.target.value === "" ? null : String(e.target.value))
			}
		>
			<MenuItem value="">All</MenuItem>
			{crews.map((crew) => (
				<MenuItem key={crew} value={crew}>
					{crew}
				</MenuItem>
			))}
		</TextField>

		<StandSelector
			required
			value={standId}
			onChange={onStandChange}
			sx={{ flex: "0 0 110px", minWidth: 110 }}
		/>

		<ResetFiltersButton onClick={onReset} disabled={!canReset} />
	</Box>
);
